// components/skeletons/LeadsListSkeleton.tsx
// Placeholder for the Leads listing page while the first page of leads loads.
// Mirrors the real layout (header card → toolbar → table rows, or cards) so
// nothing jumps when the data arrives. No props are needed; `viewMode` and
// `rows` only match what the page is about to show.

import React from 'react';
import { G, C } from '../GlobalComponents/uiTokens';
import { Bone, SkeletonStyles } from './Bone';

interface LeadsListSkeletonProps {
  viewMode?: 'list' | 'cards';
  /** how many placeholder rows / cards to draw */
  rows?: number;
}

const SHELL: React.CSSProperties = {
  background: G.shellBg,
  border: `1px solid ${G.shellBorder}`,
  borderRadius: '13px',
  padding: '4.6px',
};
const INNER: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${G.line}`,
  borderRadius: '9.2px',
};

// column widths follow the real table: checkbox · lead · phone · status · source · city · interested · assigned · created · actions
const COLS: { w: string; cell: React.ReactNode }[] = [
  { w: '2.8rem', cell: <Bone w={15} h={15} r={4} /> },
  {
    w: '166px',
    cell: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Bone w={30} h={30} r="50%" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', flex: 1 }}>
          <Bone w="70%" h={10} />
          <Bone w="45%" h={8} />
        </div>
      </div>
    ),
  },
  { w: '110px', cell: <Bone w="80%" h={10} /> },
  { w: '96px', cell: <Bone w={64} h={18} r={20} /> },
  { w: '90px', cell: <Bone w="65%" h={10} /> },
  { w: '90px', cell: <Bone w="60%" h={10} /> },
  { w: '110px', cell: <Bone w="70%" h={10} /> },
  { w: '110px', cell: <Bone w="75%" h={10} /> },
  { w: '90px', cell: <Bone w="65%" h={10} /> },
  { w: '80px', cell: <Bone w={52} h={22} r={7} /> },
];

const LeadsListSkeleton: React.FC<LeadsListSkeletonProps> = ({ viewMode = 'list', rows = 8 }) => {
  const items = Array.from({ length: rows });

  return (
    <div role="status" aria-busy="true" aria-label="Loading leads" style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
      <SkeletonStyles />

      {/* header card */}
      <div style={SHELL}>
        <div style={{ ...INNER, padding: '12px 16.6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '9.2px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
            <Bone w={46} h={8} />
            <Bone w={120} h={16} />
          </div>
          <div style={{ display: 'flex', gap: '7.4px' }}>
            <Bone w={96} h={30} r={8} />
            <Bone w={104} h={30} r={8} />
          </div>
        </div>
      </div>

      {/* filters + list */}
      <div style={SHELL}>
        <div style={INNER}>
          {/* toolbar: search, filters, view switch */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', padding: '11px 13px', borderBottom: `1px solid ${G.soft}` }}>
            <Bone w={220} h={32} r={8} />
            <Bone w={110} h={32} r={8} />
            <Bone w={110} h={32} r={8} />
            <Bone w={110} h={32} r={8} />
            <Bone w={64} h={32} r={8} style={{ marginLeft: 'auto' }} />
          </div>

          {viewMode === 'list' ? (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: '960px' }}>
                {/* table head */}
                <div style={{ display: 'flex', alignItems: 'center', background: '#fafbfb', borderBottom: `1px solid ${G.soft}` }}>
                  {COLS.map((c, i) => (
                    <div key={i} style={{ width: c.w, flex: i === 1 ? 1 : undefined, flexShrink: 0, padding: '13px' }}>
                      {i === 0 ? <Bone w={15} h={15} r={4} /> : <Bone w="50%" h={8} />}
                    </div>
                  ))}
                </div>
                {/* rows */}
                {items.map((_, r) => (
                  <div key={r} style={{ display: 'flex', alignItems: 'center', borderBottom: r === rows - 1 ? 'none' : `1px solid ${G.softer}` }}>
                    {COLS.map((c, i) => (
                      <div key={i} style={{ width: c.w, flex: i === 1 ? 1 : undefined, flexShrink: 0, padding: '10px 13px' }}>
                        {c.cell}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(231px,1fr))', gap: '11px', padding: '13px' }}>
              {items.map((_, i) => (
                <div key={i} style={{ background: G.shellBg, border: `1px solid ${G.line}`, borderRadius: '9.2px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Bone w={31} h={31} r="50%" />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', flex: 1 }}>
                      <Bone w="65%" h={10} />
                      <Bone w="45%" h={8} />
                    </div>
                    <Bone w={56} h={18} r={20} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', paddingTop: '9px', borderTop: `1px solid ${G.soft}` }}>
                    <Bone w="55%" h={8} />
                    <Bone w="70%" h={8} />
                    <Bone w="40%" h={8} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* paginator */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 13px', borderTop: `1px solid ${G.soft}` }}>
            <Bone w={120} h={10} />
            <div style={{ display: 'flex', gap: '6px' }}>
              <Bone w={28} h={28} r={7} />
              <Bone w={28} h={28} r={7} />
              <Bone w={28} h={28} r={7} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadsListSkeleton;